/**
 * Generic data table — the workhorse behind the dashboard, the triage queue and
 * the admin views. Fluid columns become labelled cards when the available
 * space is narrow. Sorting, expansion and pagination share one state across
 * both layouts, including tables nested inside an expanded detail row.
 *
 * Everything is opt-in: without `renderDetail` there is no caret column,
 * without `sortValue` on a column that header is not clickable, without
 * `onRowClick` rows are not interactive.
 */

import { Children, isValidElement, useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { TablePagination } from './TablePagination';
import './data-table.css';

export type SortDirection = 'asc' | 'desc';

/** Value a column can be sorted by. `null`/`undefined` always sort last. */
export type SortValue = string | number | boolean | null | undefined;

/** Stable identity of a row — must be unique within `rows`. */
export type RowKey = string | number;

/** One column definition. */
export interface DataTableColumn<T> {
  /** Unique within the column list; also the sort identifier. */
  key: string;
  /** Header content. German, uppercased by CSS. */
  header: ReactNode;
  /** Plain-language label for the compact layout and its sort selector. */
  mobileLabel?: string;
  /** Cell renderer. Receives the row and its index in the sorted output. */
  render: (row: T, index: number) => ReactNode;
  /** CSS width for the `<th>`, e.g. `'220px'` or `30`. */
  width?: string | number;
  /** Horizontal alignment of header and cells. Defaults to `'left'`. */
  align?: 'left' | 'center' | 'right';
  /** Providing this makes the column sortable client-side. */
  sortValue?: (row: T) => SortValue;
  /** Native tooltip on the header cell. */
  headerTitle?: string;
  /** Extra class for the body cells of this column. */
  cellClassName?: string;
}

export interface DataTableProps<T> {
  /** Rows to render, already filtered by the page. */
  rows: readonly T[];
  columns: readonly DataTableColumn<T>[];
  /** Stable key per row — also the identity used for expansion state. */
  rowKey: (row: T) => RowKey;

  /**
   * Renders the expanded detail area of a row. Presence of this prop adds the
   * caret column and makes rows clickable. Interactive cells inside a row must
   * call `event.stopPropagation()` so a button click does not toggle the row.
   */
  renderDetail?: (row: T) => ReactNode;
  /**
   * Controlled expansion. When given, `onToggleRow` must update it; when
   * omitted the table keeps its own state (which survives re-renders and
   * auto-refresh as long as `rowKey` is stable).
   */
  expandedKeys?: ReadonlySet<RowKey>;
  /** Called with the row key whenever a row is toggled. */
  onToggleRow?: (key: RowKey, row: T) => void;

  /** Initial sort. Ignored when the referenced column has no `sortValue`. */
  defaultSort?: { columnKey: string; direction?: SortDirection };
  /** German empty state, shown instead of the body when `rows` is empty. */
  emptyText?: ReactNode;
  /** Optional minimum desktop table width. Tables fit their container by default. */
  minWidth?: number | string;
  /** Available container width below which rows become labelled cards. Default 640. */
  mobileBreakpoint?: number;
  /** Per-row extra class names, e.g. to dim a deactivated user. */
  rowClassName?: (row: T) => string | undefined;
  /** Row click handler for tables without a detail area. */
  onRowClick?: (row: T) => void;
  /** Accessible caption (visually hidden). */
  caption?: string;
  /** Initial client-side page size; users can change it. Omit to show every row. */
  pageSize?: number;
  /** Resets pagination when the surrounding selection or search changes. */
  paginationResetKey?: string;
  /** Keep the current page when polling replaces rows; filter changes still reset via paginationResetKey. */
  resetPageOnRowsChange?: boolean;
  /** Reveals a linked row once per key/selection, without locking pagination. */
  revealRowKey?: RowKey | null;
}

function compare(a: SortValue, b: SortValue): number {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), 'de-DE', { numeric: true, sensitivity: 'base' });
}

const ALIGN_CLASS: Readonly<Record<'left' | 'center' | 'right', string>> = {
  left: '',
  center: 'center',
  right: 'num',
};

function headerText(node: ReactNode): string {
  return Children.toArray(node).map((child): string => {
    if (typeof child === 'string' || typeof child === 'number') return String(child);
    if (isValidElement<{ children?: ReactNode }>(child)) return headerText(child.props.children);
    return '';
  }).join('');
}

/**
 * Render a table.
 *
 * @example
 * ```tsx
 * <DataTable
 *   rows={users}
 *   rowKey={(u) => u.id}
 *   columns={[
 *     { key: 'email', header: 'E-Mail', render: (u) => u.email, sortValue: (u) => u.email },
 *     { key: 'aktiv', header: 'Aktiv', align: 'center', render: (u) => (u.active ? 'ja' : 'nein') },
 *   ]}
 *   emptyText="Keine Benutzer vorhanden."
 * />
 * ```
 */
export function DataTable<T>({
  rows,
  columns,
  rowKey,
  renderDetail,
  expandedKeys,
  onToggleRow,
  defaultSort,
  emptyText = 'Keine Daten für die aktuelle Auswahl.',
  minWidth = 0,
  mobileBreakpoint = 640,
  rowClassName,
  onRowClick,
  caption,
  pageSize,
  paginationResetKey,
  resetPageOnRowsChange = true,
  revealRowKey,
}: DataTableProps<T>): ReactNode {
  const [sort, setSort] = useState<{ columnKey: string; direction: SortDirection } | null>(
    defaultSort ? { columnKey: defaultSort.columnKey, direction: defaultSort.direction ?? 'asc' } : null,
  );
  const [internalExpanded, setInternalExpanded] = useState<ReadonlySet<RowKey>>(new Set());
  const [page, setPage] = useState(0);
  const [selectedPageSize, setSelectedPageSize] = useState<number | null>(null);
  const [compact, setCompact] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const tableId = useId();
  const revealedRow = useRef<{ key: RowKey; resetKey: string | undefined } | null>(null);
  const initialPageSize =
    pageSize !== undefined && Number.isFinite(pageSize) && pageSize >= 1
      ? Math.floor(pageSize)
      : null;
  const effectivePageSize = initialPageSize === null ? null : selectedPageSize ?? initialPageSize;
  const paginationInputs = useRef({
    rows, sort, pageSize: effectivePageSize, resetKey: paginationResetKey,
  });

  const expanded = expandedKeys ?? internalExpanded;
  const expandable = renderDetail !== undefined;

  useEffect(() => {
    const element = container.current;
    if (!element) return;
    // Measure the actual space beside navigation or inside an expanded row,
    // rather than assuming every table has the full viewport available.
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setCompact(entry.contentRect.width <= mobileBreakpoint);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [mobileBreakpoint]);

  const toggleRow = useCallback(
    (key: RowKey, row: T): void => {
      if (onToggleRow) {
        onToggleRow(key, row);
        return;
      }
      setInternalExpanded((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [onToggleRow],
  );

  const sortedRows = useMemo<readonly T[]>(() => {
    if (!sort) return rows;
    const column = columns.find((candidate) => candidate.key === sort.columnKey);
    const sortValue = column?.sortValue;
    if (!sortValue) return rows;
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => factor * compare(sortValue(a), sortValue(b)));
  }, [rows, columns, sort]);

  useEffect(() => {
    const previous = paginationInputs.current;
    if (
      (!resetPageOnRowsChange || previous.rows === rows) &&
      previous.sort === sort &&
      previous.pageSize === effectivePageSize &&
      previous.resetKey === paginationResetKey
    ) {
      return;
    }
    paginationInputs.current = { rows, sort, pageSize: effectivePageSize, resetKey: paginationResetKey };
    setPage(0);
  }, [rows, sort, effectivePageSize, paginationResetKey, resetPageOnRowsChange]);

  useEffect(() => {
    if (revealRowKey === undefined || revealRowKey === null || effectivePageSize === null) {
      revealedRow.current = null;
      return;
    }
    if (
      revealedRow.current?.key === revealRowKey &&
      revealedRow.current.resetKey === paginationResetKey
    ) {
      return;
    }
    revealedRow.current = null;

    // Wait for asynchronously loaded rows before consuming the request. A
    // subsequent manual page change must not reveal the same row again.
    const index = sortedRows.findIndex((row) => rowKey(row) === revealRowKey);
    if (index < 0) return;
    revealedRow.current = { key: revealRowKey, resetKey: paginationResetKey };
    setPage(Math.floor(index / effectivePageSize));
  }, [effectivePageSize, paginationResetKey, revealRowKey, rowKey, sortedRows]);

  const pageCount = effectivePageSize === null
    ? 1
    : Math.max(1, Math.ceil(sortedRows.length / effectivePageSize));
  const currentPage = Math.min(page, pageCount - 1);
  useEffect(() => {
    setPage((current) => Math.min(current, pageCount - 1));
  }, [pageCount]);
  const firstRowIndex = effectivePageSize === null ? 0 : currentPage * effectivePageSize;
  const visibleRows = effectivePageSize === null
    ? sortedRows
    : sortedRows.slice(firstRowIndex, firstRowIndex + effectivePageSize);

  const onHeaderClick = useCallback((column: DataTableColumn<T>): void => {
    if (!column.sortValue) return;
    setSort((current) => {
      if (current?.columnKey !== column.key) return { columnKey: column.key, direction: 'asc' };
      if (current.direction === 'asc') return { columnKey: column.key, direction: 'desc' };
      return null;
    });
  }, []);

  const totalColumns = columns.length + (expandable ? 1 : 0);
  const sortableColumns = columns.filter((column) => column.sortValue !== undefined);

  const changePage = (next: number): void => {
    setPage(Math.max(0, Math.min(next, pageCount - 1)));
    // A shorter last page must not strand the reader below the new rows.
    // Automatic refreshes and filters never move the viewport.
    if (container.current && container.current.getBoundingClientRect().top < 0) {
      requestAnimationFrame(() => {
        const element = container.current;
        if (!element) return;
        element.scrollIntoView({ block: 'start', behavior: 'instant' });
        // Pagination sits below the table only; keyboard focus moves to the
        // start of the new page instead of staying on the off-screen bar.
        element.focus({ preventScroll: true });
      });
    }
  };

  // Below the table only: the toolbar above already carries the row count.
  // A single page at the default size needs no bar; once the reader picked
  // another size it stays, so they can switch back.
  const pagination = (): ReactNode => effectivePageSize === null || initialPageSize === null
    || (pageCount <= 1 && effectivePageSize === initialPageSize) ? null : (
    <TablePagination
      page={currentPage}
      pageCount={pageCount}
      pageSize={effectivePageSize}
      initialPageSize={initialPageSize}
      total={sortedRows.length}
      caption={caption}
      onPageChange={changePage}
      onPageSizeChange={(size) => { setSelectedPageSize(size); changePage(0); }}
    />
  );

  return (
    <div ref={container} tabIndex={-1} className={`data-table${compact ? ' data-table-compact' : ''}`}>
      {sortableColumns.length > 0 ? (
        <div className="data-table-sort-controls">
          <label htmlFor={`${tableId}-sort`}>Sortieren nach</label>
          <select
            id={`${tableId}-sort`}
            value={sort?.columnKey ?? ''}
            onChange={(event) => setSort(event.target.value
              ? { columnKey: event.target.value, direction: sort?.direction ?? 'asc' }
              : null)}
          >
            <option value="">Standardreihenfolge</option>
            {sortableColumns.map((column) => (
              <option key={column.key} value={column.key}>
                {column.mobileLabel ?? headerText(column.header)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="ghost data-table-sort-direction"
            disabled={!sort}
            aria-label={sort?.direction === 'desc' ? 'Aufsteigend sortieren' : 'Absteigend sortieren'}
            title={sort?.direction === 'desc' ? 'Aufsteigend sortieren' : 'Absteigend sortieren'}
            onClick={() => setSort((current) => current
              ? { ...current, direction: current.direction === 'asc' ? 'desc' : 'asc' }
              : null)}
          >
            <span aria-hidden="true">{sort?.direction === 'desc' ? '↓' : '↑'}</span>
          </button>
        </div>
      ) : null}
      <div className="tablewrap">
        <table
          className="data-table-grid"
          role="table"
          style={{ minWidth: compact ? 0 : minWidth }}
        >
          {caption ? <caption className="visually-hidden">{caption}</caption> : null}
          <thead role="rowgroup">
            <tr role="row">
              {expandable ? <th scope="col" role="columnheader" style={{ width: 44 }} aria-label="Aufklappen" /> : null}
              {columns.map((column) => {
                const sortable = column.sortValue !== undefined;
                const active = sort?.columnKey === column.key;
                const alignClass = ALIGN_CLASS[column.align ?? 'left'];
                const classes = [alignClass, sortable ? 'sortable' : '', active ? 'sorted' : '']
                  .filter(Boolean)
                  .join(' ');
                const mark = active ? (
                  <span className="sortmark" aria-hidden="true">
                    {sort.direction === 'asc' ? '▲' : '▼'}
                  </span>
                ) : null;
                return (
                  <th
                    key={column.key}
                    scope="col"
                    role="columnheader"
                    className={classes || undefined}
                    style={column.width === undefined ? undefined : { width: column.width }}
                    title={column.headerTitle}
                    aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {/*
                      A sortable header must be operable by keyboard, so the click
                      target is a real <button> inside the <th> — putting the
                      handler on the <th> itself (and giving it a role) would break
                      the columnheader semantics screen readers rely on for
                      `aria-sort`.
                    */}
                    {sortable ? (
                      <button type="button" className="th-sort" tabIndex={compact ? -1 : undefined} onClick={() => onHeaderClick(column)}>
                        {column.header}
                        {mark}
                      </button>
                    ) : (
                      <>
                        {column.header}
                        {mark}
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody role="rowgroup">
            {sortedRows.length === 0 ? (
              <tr role="row" className="data-table-empty-row">
                <td role="cell" colSpan={totalColumns}>
                  <div className="empty">{emptyText}</div>
                </td>
              </tr>
            ) : (
              visibleRows.map((row, pageIndex) => {
                const index = firstRowIndex + pageIndex;
                const key = rowKey(row);
                const detailId = `${tableId}-detail-${encodeURIComponent(String(key))}`;
                const isOpen = expanded.has(key);
                const interactive = expandable || onRowClick !== undefined;
                const classes = [
                  'data-table-row',
                  expandable ? 'group' : '',
                  expandable && isOpen ? 'open' : '',
                  // Banding comes from the row index rather than a CSS
                  // `:nth-child`, because an expanded detail row sits between two
                  // group rows and would flip the parity of every row below it.
                  index % 2 === 1 ? 'alt' : '',
                  rowClassName?.(row) ?? '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return [
                  <tr
                    key={`row-${key}`}
                    role="row"
                    className={classes || undefined}
                    tabIndex={!expandable && onRowClick ? 0 : undefined}
                    onKeyDown={!expandable && onRowClick ? (event) => {
                      if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        onRowClick(row);
                      }
                    } : undefined}
                    onClick={
                      interactive
                        ? () => {
                            if (expandable) toggleRow(key, row);
                            onRowClick?.(row);
                          }
                        : undefined
                    }
                    style={!expandable && onRowClick ? { cursor: 'pointer' } : undefined}
                  >
                    {expandable ? (
                      <td className="caret" role="cell">
                        <button
                          type="button"
                          className="link data-table-expand"
                          aria-expanded={isOpen}
                          aria-controls={isOpen ? detailId : undefined}
                          aria-label={isOpen ? 'Details einklappen' : 'Details aufklappen'}
                          onClick={(event) => {
                            // The row handler already toggles; keep the button from
                            // toggling a second time.
                            event.stopPropagation();
                            toggleRow(key, row);
                          }}
                        >
                          <svg
                            className="tw" width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                            strokeLinejoin="round" aria-hidden="true"
                          >
                            <path d="m9 5 7 7-7 7" />
                          </svg>
                          <span className="data-table-expand-label">{isOpen ? 'Details einklappen' : 'Details aufklappen'}</span>
                        </button>
                      </td>
                    ) : null}
                    {columns.map((column, columnIndex) => {
                      const alignClass = ALIGN_CLASS[column.align ?? 'left'];
                      // The column the table is sorted by gets a rail on both
                      // sides — it is the one the user is actually reading down.
                      const classNames = [
                        columnIndex === 0 ? 'data-table-primary-cell' : '',
                        alignClass,
                        sort?.columnKey === column.key ? 'sorted' : '',
                        column.cellClassName ?? '',
                      ]
                        .filter(Boolean)
                        .join(' ');
                      return (
                        <td key={column.key} role="cell" className={classNames || undefined}>
                          <span className="data-table-cell-label" aria-hidden="true">
                            {column.mobileLabel ?? headerText(column.header)}
                          </span>
                          <div className="data-table-cell-content">{column.render(row, index)}</div>
                        </td>
                      );
                    })}
                  </tr>,
                  expandable && isOpen ? (
                    <tr key={`detail-${key}`} id={detailId} className="detail" role="row">
                      <td role="cell" colSpan={totalColumns}>{renderDetail?.(row)}</td>
                    </tr>
                  ) : null,
                ];
              })
            )}
          </tbody>
        </table>
      </div>
      {pagination()}
    </div>
  );
}

export default DataTable;
